import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import Box from '~/ui/components/Box/Box';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import PageLayout from '~/ui/components/PageLayout/PageLayout';
import Text from '~/ui/components/Text/Text';

function AribaHeader() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const manageSyncPairStage = useSelector(
    slices.integrations.selectors.selectManageSyncPairStage,
  );

  const stagesOneTimeImport = [
    {
      label: intl.formatMessage({
        id: 'AribaHeader.apiConfigurations',
        defaultMessage: 'API Configurations',
        description: 'Label for API configurations stage',
      }),
      value: ManageSyncPairStageType.MultiAPIs,
    },
    {
      label: intl.formatMessage({
        id: 'AribaHeader.fileConfigurations',
        defaultMessage: 'File Configurations',
        description: 'Label for file configurations stage',
      }),
      value: ManageSyncPairStageType.FileConfig,
    },
    {
      label: intl.formatMessage({
        id: 'AribaHeader.fileConfigurationsFolder',
        defaultMessage: 'File Configurations',
        description: 'Label for file configurations folder stage',
      }),
      value: ManageSyncPairStageType.FolderAriba,
    },
    {
      label: intl.formatMessage({
        id: 'AribaHeader.fieldDataMapping',
        defaultMessage: 'Field Data Mapping',
        description: 'Label for field data mapping stage',
      }),
      value: ManageSyncPairStageType.FieldMapping,
    },
    {
      label: intl.formatMessage({
        id: 'AribaHeader.summary',
        defaultMessage: 'Summary',
        description: 'Label for summary stage',
      }),
      value: ManageSyncPairStageType.SummaryAriba,
    },
  ];

  const stagesReauth = [
    {
      label: intl.formatMessage({
        id: 'AribaHeader.apiConfigurationsReauth',
        defaultMessage: 'API Configurations',
        description: 'Label for API configurations reauthentication stage',
      }),
      value: ManageSyncPairStageType.MultiAPIs,
    },
  ];

  const isReauthenticate = !!activeSyncPair?.isTokenReauthenticate;

  const getStages = () => {
    if (isReauthenticate) {
      return stagesReauth;
    }
    return stagesOneTimeImport;
  };

  const stages = getStages();
  const prefix = activeSyncPair ? `${activeSyncPair.name} - ` : '';
  const title = `${prefix}${
    isReauthenticate
      ? intl.formatMessage({
          id: 'AribaHeader.reauthenticate',
          defaultMessage: 'Reauthenticate',
          description: 'Title for reauthentication process',
        })
      : intl.formatMessage({
          id: 'AribaHeader.newSyncPair',
          defaultMessage: 'New Sync Pair',
          description: 'Title for new sync pair creation',
        })
  }`;

  const handleClose = () => {
    dispatch(slices.integrations.actions.setActiveSyncPair(null));
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.None,
      ),
    );
  };
  const centerContent = (
    <FlexLayout alignItems="center" space={3}>
      {stages
        .filter((stage, index, self) => {
          // The purpose of this is to hide the duplicate stages by label
          // and only show the active one.

          const activeStage = manageSyncPairStage;
          const similarStages = self.filter((s) => s.label === stage.label);
          const hasDuplicates = similarStages.length > 1;

          if (hasDuplicates) {
            const isActive = similarStages
              .map((stage) => stage.value)
              .includes(activeStage);

            // If it's active, return the active stage
            if (isActive) return stage.value === activeStage;
            // Since it's for presentation purposes, it's okay to return the first stage
            // The other will remain hidden
            if (!isActive) return stage === similarStages[0];
          }

          return true;
        })
        .map((stage, i) => {
          const isActiveStage = stage.value === manageSyncPairStage;
          return (
            <Fragment key={stage.value}>
              <Text
                color="gray-800"
                disabled={!isActiveStage}
                variant={isActiveStage ? 's-dense-bold' : 's-dense'}
              >
                {stage.label}
              </Text>
              {i < stages.length - 1 && (
                <Box sx={{ width: '24px' }}>
                  <Divider />
                </Box>
              )}
            </Fragment>
          );
        })}
    </FlexLayout>
  );

  return (
    <PageLayout.Header
      title={{
        text: title,
      }}
      centerContent={centerContent}
      onClose={handleClose}
    />
  );
}

export default AribaHeader;
